import {useCallback, useMemo, useState} from 'preact/hooks';

import {Theme} from 'utilities/color';
import {JSXNode} from 'utilities/props';

import {LayoutProps, UiLineType} from '../../types';
import {
  LayoutContext,
  LayoutContextInterface,
  UILineWrapper,
} from '../../context';

import styles from './AmazonLayout.css';

export default function AmazonLayout({
  isLoading,
  isHidden,
  theme,
  children,
}: LayoutProps) {
  const classes = useMemo(
    () => new Map([[UiLineType.FullSize, styles.fullSize]]),
    [],
  );

  const wrapElement: UILineWrapper = useCallback(
    (type, children) => {
      if (!children) {
        return null;
      }
      const style = classes.get(type);
      if (style) {
        return <div className={style}>{children}</div>;
      }
      return children;
    },
    [classes],
  );

  const [contentSlot, setContentSlot] = useState<JSXNode>(null);
  const [learnMoreSlot, setLearnMoreSlot] = useState<JSXNode>(null);

  const layoutContext = useMemo(
    () =>
      ({
        lineWrapper: wrapElement,
        onSlotAdded: (name, slotContent) => {
          switch (name) {
            case 'Content':
              return setContentSlot(slotContent);
            case 'LearnMore':
              return setLearnMoreSlot(slotContent);
          }
        },
      } as LayoutContextInterface),
    [wrapElement],
  );

  const content: JSXNode = (
    // eslint-disable-next-line @shopify/jsx-prefer-fragment-wrappers
    <div>
      {children}
      <div className={styles.content}>
        <div className={styles.contentSlot}>
          {contentSlot} {learnMoreSlot}
        </div>
      </div>
    </div>
  );

  const markup: JSXNode = (
    <div
      className={[
        styles.amazonLayout,
        theme === Theme.Dark ? styles.Dark : '',
        isHidden ? styles.Hidden : '',
      ].join(' ')}
    >
      <div
        className={[styles.details, isLoading ? styles.IsLoading : ''].join(
          ' ',
        )}
      >
        {content}
      </div>
    </div>
  ) as unknown as JSXNode;

  return (
    <LayoutContext.Provider value={layoutContext}>
      {markup}
    </LayoutContext.Provider>
  );
}
